import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeDefinition } from './theme.model';
import {
  THEME_PERSISTENCE,
  ThemePersistence,
} from './theme.persistence';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly documentRef = inject(DOCUMENT);
  private readonly persistence = inject(THEME_PERSISTENCE, { optional: true });
  private readonly themes = new Map<string, ThemeDefinition>();
  private readonly currentThemeSubject =
    new BehaviorSubject<ThemeDefinition | null>(null);

  readonly currentTheme$ = this.currentThemeSubject.asObservable();

  registerThemes(themes: ThemeDefinition[]): void {
    for (const theme of themes) {
      this.themes.set(theme.id, theme);
    }
  }

  setTheme(themeId: string): void {
    const theme = this.themes.get(themeId);

    if (!theme) {
      return;
    }

    this.applyTheme(theme);
    this.currentThemeSubject.next(theme);
    this.persistence?.save(theme.id);
  }

  initialize(defaultThemeId: string): void {
    const persistedThemeId = this.persistence?.load();
    const initialThemeId =
      persistedThemeId && this.themes.has(persistedThemeId)
        ? persistedThemeId
        : defaultThemeId;

    this.setTheme(initialThemeId);
  }

  getCurrentTheme(): ThemeDefinition | null {
    return this.currentThemeSubject.value;
  }

  getAvailableThemes(): ThemeDefinition[] {
    return [...this.themes.values()];
  }

  private applyTheme(theme: ThemeDefinition): void {
    const root = this.documentRef.documentElement;

    for (const [tokenName, tokenValue] of Object.entries(theme.tokens)) {
      root.style.setProperty(this.toCssCustomPropertyName(tokenName), tokenValue);
    }

    this.applyLegacyAliases(theme);
    root.setAttribute('data-theme', theme.id);
    root.setAttribute('data-color-scheme', theme.colorScheme);
    root.classList.toggle('dark', theme.colorScheme === 'dark');
  }

  private applyLegacyAliases(theme: ThemeDefinition): void {
    const root = this.documentRef.documentElement;
    const { tokens } = theme;

    root.style.setProperty('--color-bg', tokens.colorBackground);
    root.style.setProperty('--color-text', tokens.colorTextPrimary);
    root.style.setProperty('--color-text-muted', tokens.colorTextSecondary);
    root.style.setProperty('--color-border', tokens.colorAccent);
    root.style.setProperty('--color-primary-500', tokens.colorPrimary);
    root.style.setProperty('--color-primary-600', tokens.colorPrimary);
    root.style.setProperty('--color-secondary-500', tokens.colorSecondary);
    root.style.setProperty('--color-secondary-600', tokens.colorSecondary);
    root.style.setProperty('--color-success-500', tokens.colorSuccess);
    root.style.setProperty('--color-success-600', tokens.colorSuccess);
    root.style.setProperty('--color-warning-500', tokens.colorWarning);
    root.style.setProperty('--color-warning-600', tokens.colorWarning);
    root.style.setProperty('--color-error-500', tokens.colorError);
    root.style.setProperty('--color-error-600', tokens.colorError);
  }

  private toCssCustomPropertyName(tokenName: string): string {
    return `--${tokenName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()}`;
  }
}